import { useState } from 'react';
import { apiClient } from '@/lib/api-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Store, Plus, Loader2, Check } from 'lucide-react';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { toast } from 'sonner';
import type { components } from '@/api/schema';
import { apiErrorMessage } from '@/lib/api-error';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { intlLocale } from './adminLocale';

type MerchantResource = components['schemas']['MerchantResource'];
type MerchantPlatformStatus = components['schemas']['MerchantPlatformStatus'];

const statusColors: Record<string, string> = {
  new: 'bg-purple-100 text-purple-800',
  active: 'bg-green-100 text-green-800',
  blocked: 'bg-red-100 text-red-800',
  frozen: 'bg-blue-100 text-blue-800',
  pending: 'bg-yellow-100 text-yellow-800',
  rejected: 'bg-red-100 text-red-800',
};

const statusLabels = (): Record<string, string> => ({
  new: i18n.t('adm_merchants.st_new'),
  active: i18n.t('adm_merchants.st_active'),
  blocked: i18n.t('adm_merchants.st_blocked'),
  frozen: i18n.t('adm_merchants.st_frozen'),
  pending: i18n.t('adm_merchants.st_pending'),
  rejected: i18n.t('adm_merchants.st_rejected'),
});

const trustLevelColors: Record<string, string> = {
  green: 'bg-green-500',
  yellow: 'bg-yellow-500',
  red: 'bg-red-500',
};

const trustLevelLabels = (): Record<string, string> => ({
  green: i18n.t('adm_merchants.trust_green'),
  yellow: i18n.t('adm_merchants.trust_yellow'),
  red: i18n.t('adm_merchants.trust_red'),
});

const trustLevelTextColors: Record<string, string> = {
  green: 'text-green-600',
  yellow: 'text-yellow-600',
  red: 'text-red-600',
};

// Mirrors MerchantStatusTransitionService::ALLOWED (apps/api) — the one-step
// "next status" this list offers per row. Anything needing a reason (reject,
// block, freeze) or a non-linear choice stays on the merchant detail page.
const NEXT_STEP: Partial<Record<MerchantPlatformStatus, MerchantPlatformStatus>> = {
  new: 'pending',
  pending: 'active',
};
const nextStepLabel = (): Record<string, string> => ({
  pending: i18n.t('adm_merchants.next_pending'),
  active: i18n.t('adm_merchants.next_active'),
});

export default function AdminMerchants() {
  const { t } = useTranslation();
  const { admin } = useAdminAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<string>('all');
  const [advancing, setAdvancing] = useState<number | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['/admin/merchants'] });
  const merchants: MerchantResource[] = data?.data ?? [];

  const canChangeStatus = admin?.role === 'platform_owner' || admin?.role === 'support_admin';

  const advanceStatus = async (merchant: MerchantResource) => {
    const to = NEXT_STEP[merchant.platform_status as MerchantPlatformStatus];
    if (!to) return;
    setAdvancing(merchant.id);
    try {
      const { error } = await apiClient.PATCH('/admin/merchants/{merchant}/status', {
        params: { path: { merchant: merchant.id } },
        body: { to_status: to, reason: `${nextStepLabel()[to]} (${admin?.full_name ?? 'admin'})` },
      });
      if (error) throw error;
      toast.success(t('adm_merchants.statusChanged', { status: statusLabels()[to] }));
      await queryClient.invalidateQueries({ queryKey: ['/admin/merchants'] });
    } catch (e) {
      toast.error(apiErrorMessage(e, t('adm_merchants.statusChangeFailed')));
    } finally {
      setAdvancing(null);
    }
  };

  const filterDisplayLabels: Record<string, string> = {
    all: t('adm_common.all'),
    's:new': t('adm_merchants.st_new'), 's:active': t('adm_merchants.st_active'), 's:blocked': t('adm_merchants.st_blocked'),
    's:frozen': t('adm_merchants.st_frozen'), 's:pending': t('adm_merchants.st_pending'), 's:rejected': t('adm_merchants.st_rejected'),
    't:green': `🟢 ${t('adm_merchants.trust_green')}`, 't:yellow': `🟡 ${t('adm_merchants.trust_yellow')}`, 't:red': `🔴 ${t('adm_merchants.trust_red')}`, 't:none': t('adm_merchants.trust_none'),
  };

  const filtered = merchants.filter(m => {
    const matchSearch = !search ||
      m.name?.toLowerCase().includes(search.toLowerCase()) ||
      m.inn?.includes(search) ||
      m.phone?.includes(search);
    if (filter === 'all') return matchSearch;
    if (filter.startsWith('s:')) return matchSearch && m.platform_status === filter.slice(2);
    if (filter === 't:none') return matchSearch && !m.trust_level;
    if (filter.startsWith('t:')) return matchSearch && m.trust_level === filter.slice(2);
    return matchSearch;
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('adm_merchants.title')}</h1>
          <p className="text-muted-foreground text-sm">{t('adm_merchants.subtitle')}</p>
        </div>
        {(admin?.role === 'platform_owner' || admin?.role === 'support_admin') && (
          <Button size="sm" onClick={() => navigate('/admin/merchants/new')}>
            <Plus size={16} className="mr-1" /> {t('adm_merchants.newMerchant')}
          </Button>
        )}
      </div>

      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t('adm_merchants.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue>{filterDisplayLabels[filter] || t('adm_common.all')}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('adm_common.all')}</SelectItem>
            <SelectGroup>
              <SelectLabel>{t('adm_merchants.status')}</SelectLabel>
              <SelectItem value="s:new">{t('adm_merchants.st_new')}</SelectItem>
              <SelectItem value="s:active">{t('adm_merchants.st_active')}</SelectItem>
              <SelectItem value="s:blocked">{t('adm_merchants.st_blocked')}</SelectItem>
              <SelectItem value="s:frozen">{t('adm_merchants.st_frozen')}</SelectItem>
              <SelectItem value="s:pending">{t('adm_merchants.st_pending')}</SelectItem>
              <SelectItem value="s:rejected">{t('adm_merchants.st_rejected')}</SelectItem>
            </SelectGroup>
            <SelectGroup>
              <SelectLabel>{t('adm_merchants.trust')}</SelectLabel>
              <SelectItem value="t:green">🟢 {t('adm_merchants.trust_green')}</SelectItem>
              <SelectItem value="t:yellow">🟡 {t('adm_merchants.trust_yellow')}</SelectItem>
              <SelectItem value="t:red">🔴 {t('adm_merchants.trust_red')}</SelectItem>
              <SelectItem value="t:none">{t('adm_merchants.trust_none')}</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground py-8">{t('adm_common.loading')}</div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('adm_merchants.storeName')}</TableHead>
                <TableHead>{t('adm_merchants.legalName')}</TableHead>
                <TableHead>INN</TableHead>
                <TableHead>{t('adm_merchants.phone')}</TableHead>
                <TableHead>{t('adm_merchants.status')}</TableHead>
                <TableHead>{t('adm_merchants.trust')}</TableHead>
                <TableHead>{t('adm_merchants.created')}</TableHead>
                {canChangeStatus && <TableHead className="text-right">{t('adm_merchants.actions')}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canChangeStatus ? 8 : 7} className="text-center text-muted-foreground py-8">
                    <Store className="mx-auto mb-2 h-8 w-8 opacity-30" />
                    {t('adm_merchants.notFound')}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map(m => {
                  const next = NEXT_STEP[m.platform_status as MerchantPlatformStatus];
                  return (
                    <TableRow key={m.id} className="cursor-pointer" onClick={() => navigate(`/admin/merchants/${m.id}`)}>
                      <TableCell className="font-medium">{m.name}</TableCell>
                      <TableCell>{m.legal_name}</TableCell>
                      <TableCell className="font-mono text-xs">{m.inn}</TableCell>
                      <TableCell>{m.phone}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={statusColors[m.platform_status] || ''}>
                          {statusLabels()[m.platform_status] || m.platform_status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {m.trust_level && trustLevelColors[m.trust_level] ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span className={`inline-block w-3 h-3 rounded-full ${trustLevelColors[m.trust_level]}`} />
                            <span className={`text-xs font-medium ${trustLevelTextColors[m.trust_level] || ''}`}>
                              {trustLevelLabels()[m.trust_level] || m.trust_level}
                            </span>
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {m.created_at ? new Date(m.created_at).toLocaleDateString(intlLocale()) : '—'}
                      </TableCell>
                      {canChangeStatus && (
                        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                          {next && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={advancing === m.id}
                              onClick={() => advanceStatus(m)}
                            >
                              {advancing === m.id ? (
                                <Loader2 size={14} className="animate-spin mr-1" />
                              ) : (
                                <Check size={14} className="mr-1" />
                              )}
                              {nextStepLabel()[next]}
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
